import { useEffect, useMemo, useState } from 'react'
import Layout from '../components/Layout'
import { auth } from '../firebase'
import { subscribeParentChildren, subscribeParent, unlinkChildFromParent } from '../lib/db'
import { formatScreenLock, appInitial, timeAgo, isOnline } from '../lib/format'
import { toast } from '../lib/toast'
import type { Child, Parent } from '../lib/types'
import type { NavDest, Tab } from '../lib/nav'

export default function DashboardPage({
  onOpenChild,
  isAdmin,
  onOpenAdmin,
  onOpenAccount,
  onOpenDownloads,
  trialDays,
}: {
  onOpenChild: (uid: string, tab?: Tab) => void
  isAdmin?: boolean
  onOpenAdmin?: () => void
  onOpenAccount?: () => void
  onOpenDownloads?: () => void
  trialDays?: number
}) {
  const [children, setChildren] = useState<{ uid: string; data: Child }[]>([])
  const [loaded, setLoaded] = useState(false)
  const [parent, setParent] = useState<Parent | null>(null)
  const parentUid = auth.currentUser?.uid ?? ''

  useEffect(() => {
    if (!parentUid) return
    const un1 = subscribeParentChildren(parentUid, (list) => {
      setChildren(list)
      setLoaded(true)
    })
    const un2 = subscribeParent(parentUid, setParent)
    return () => {
      un1()
      un2()
    }
  }, [parentUid])

  const handleNav = (dest: NavDest) => {
    if (dest === 'account') {
      onOpenAccount?.()
      return
    }
    if (dest === 'downloads') {
      onOpenDownloads?.()
      return
    }
    if (dest === 'home') {
      document.getElementById('app-main')?.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    // activity / apps necesitan un dispositivo vinculado
    if (children.length === 0) {
      toast('Primero instala la app en un dispositivo desde el módulo Descargas.', 'error')
      onOpenDownloads?.()
      return
    }
    onOpenChild(children[0].uid, dest === 'apps' ? 'apps' : 'activity')
  }

  const stats = useMemo(() => {
    const allApps = children.flatMap((c) => c.data.apps ?? [])
    const blocked = allApps.filter((a) => a.blocked).length
    const alerts = children.filter((c) => c.data.location?.outOfFence).length
    return {
      children: children.length,
      apps: allApps.length,
      blocked,
      alerts,
      online: children.filter((c) => isOnline(c.data.lastSeen)).length,
    }
  }, [children])

  return (
    <Layout
      title="Inicio"
      onNavigate={handleNav}
      isAdmin={isAdmin}
      onAdmin={onOpenAdmin}
      onAccount={onOpenAccount}
      onDownloads={onOpenDownloads}
    >
      {trialDays !== undefined && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎁</span>
            <div>
              <p className="font-semibold text-sky-800">
                Estás en período de prueba gratuita
              </p>
              <p className="text-sm text-sky-600">
                Te quedan {trialDays} {trialDays === 1 ? 'día' : 'días'} para renovar tu suscripción.
              </p>
            </div>
          </div>
          <span className="rounded-full bg-sky-200 px-3 py-1 text-sm font-bold text-sky-800">
            {trialDays} {trialDays === 1 ? 'día' : 'días'}
          </span>
        </div>
      )}

      <div className="mb-6 rounded-2xl bg-gradient-to-r from-brand-600 to-brand-700 px-6 py-6 text-white shadow-md">
        <h2 className="text-xl font-bold">
          Hola, {parent?.name?.split(' ')[0] ?? 'familia'} 👋
        </h2>
        <p className="mt-1 text-sm text-brand-100">
          Este es el resumen de la actividad de tus dispositivos en tiempo real.
        </p>
      </div>

      {!loaded ? (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="animate-pulse rounded-2xl bg-white p-5 ring-1 ring-slate-200">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 rounded bg-slate-200" />
                  <div className="h-3 w-1/2 rounded bg-slate-200" />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[0, 1, 2].map((j) => (
                  <div key={j} className="h-14 rounded-xl bg-slate-100" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : children.length === 0 ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-brand-100 text-4xl">
            👶
          </div>
          <h2 className="text-xl font-bold text-slate-700">Aún no tienes dispositivos vinculados</h2>
          <p className="mt-2 max-w-md text-sm text-slate-500">
            Instala la app Android de KidSafe o el agente Windows con el correo de este padre, y
            aparecerán aquí automáticamente.
          </p>
          {onOpenDownloads && (
            <button
              type="button"
              onClick={onOpenDownloads}
              className="mt-5 rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white shadow-sm transition hover:bg-brand-700 active:bg-brand-800"
            >
              📥 Descargar la app
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <SummaryCard label="Dispositivos" value={stats.children} icon="📱" />
            <SummaryCard label="En línea" value={stats.online} icon="🟢" accent="text-emerald-600" />
            <SummaryCard label="Apps monitorizadas" value={stats.apps} icon="🧩" />
            <SummaryCard label="Bloqueadas" value={stats.blocked} icon="🚫" />
            <SummaryCard
              label="Alertas"
              value={stats.alerts}
              icon="⚠️"
              accent={stats.alerts > 0 ? 'text-rose-600' : 'text-slate-700'}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {children.map(({ uid, data }) => (
              <ChildCard
                key={uid}
                child={data}
                childUid={uid}
                onOpen={() => onOpenChild(uid)}
                onDelete={async (childUid) => {
                  if (!confirm('¿Eliminar este dispositivo? Se desvinculará de tu cuenta y dejará de reportar actividad.')) return
                  try {
                    await unlinkChildFromParent(parentUid, childUid)
                    toast('🗑️ Dispositivo eliminado', 'success')
                  } catch {
                    toast('No se pudo eliminar el dispositivo', 'error')
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}
    </Layout>
  )
}

function SummaryCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: number
  icon: string
  accent?: string
}) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <p className={`text-2xl font-bold ${accent ?? 'text-slate-800'}`}>{value}</p>
          <p className="text-xs text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

function ChildCard({
  child,
  childUid,
  onDelete,
  onOpen,
}: {
  child: Child
  childUid: string
  onDelete: (uid: string) => void
  onOpen: () => void
}) {
  const apps = child.apps ?? []
  const blockedCount = apps.filter((a) => a.blocked).length
  const hasLocation =
    typeof child.location?.latitude === 'number' && typeof child.location?.longitude === 'number'
  const outOfFence = child.location?.outOfFence
  const online = isOnline(child.lastSeen)
  const isWindows = child.deviceType === 'windows'
  const activeApp = child.windows?.activeApp

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      className="group cursor-pointer rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 transition hover:shadow-md active:scale-[0.99]"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-100 text-2xl font-bold text-brand-700">
            {child.profileImage ? (
              <img src={child.profileImage} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              appInitial(child.name)
            )}
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ring-2 ring-white ${
                online ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
            />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-slate-800">
              {child.name} <span className="text-sm font-normal">{isWindows ? '🖥️' : '🤖'}</span>
            </h3>
            <p className="truncate text-sm text-slate-500">{child.email}</p>
            <p className="mt-0.5 text-xs text-slate-400">
              {online ? 'En línea' : `Visto ${timeAgo(child.lastSeen)}`}
              {activeApp ? ` · usando ${activeApp}` : ''}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(childUid)
          }}
          className="flex-shrink-0 ml-3 rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
          title="Eliminar dispositivo"
        >
          🗑️
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat label="Apps" value={String(apps.length)} />
        <Stat label="Bloqueadas" value={String(blockedCount)} />
        <Stat label="Tiempo máx." value={formatScreenLock(child.screenLock)} />
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        {hasLocation ? (
          <span className="flex items-center gap-1 text-slate-500">
            📍 {child.location?.latitude?.toFixed(4)}, {child.location?.longitude?.toFixed(4)}
          </span>
        ) : (
          <span className="text-slate-400">{isWindows ? '🖥️ PC de escritorio' : '📍 Sin ubicación'}</span>
        )}
        {outOfFence ? (
          <span className="rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-600">
            Fuera de la zona
          </span>
        ) : (
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-600">
            En zona segura
          </span>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-slate-50 px-2 py-2">
      <p className="text-sm font-bold text-slate-800">{value}</p>
      <p className="text-[11px] text-slate-400">{label}</p>
    </div>
  )
}
